// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import ScoreFactor from "./score-factor.js"
import SurveyStepElementOption from "./survey-step-element-option.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "score": {
      "column": {
        "default": null,
        "name": "score",
        "null": false,
        "type": "integer"
      },
      "name": "score",
      "selected_by_default": null,
      "translated": null
    },
    "score_factor_id": {
      "column": {
        "default": null,
        "name": "score_factor_id",
        "null": false,
        "type": null
      },
      "name": "score_factor_id",
      "selected_by_default": null,
      "translated": null
    },
    "survey_step_element_option_id": {
      "column": {
        "default": null,
        "name": "survey_step_element_option_id",
        "null": false,
        "type": null
      },
      "name": "survey_step_element_option_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "SurveyStepElementOptionScore",
  "collectionKey": "survey_step_element_option_scores",
  "collectionName": "survey_step_element_option_scores",
  "i18nKey": "survey_step_element_option_score",
  "camelizedLower": "surveyStepElementOptionScore",
  "name": "SurveyStepElementOptionScore",
  "nameDasherized": "survey-step-element-option-score",
  "pluralName": "survey_step_element_option_scores",
  "ransackable_associations": [
    {
      "className": "SurveyStepElementOptionScore::Audit",
      "collectionName": "survey_step_element_option_score_audits",
      "foreignKey": "survey_step_element_option_score_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "SurveyStepElementOptionScoreAudit",
      "through": null
    },
    {
      "className": "SurveyStepElementOptionScore::Audit",
      "collectionName": "survey_step_element_option_score_audits",
      "foreignKey": "survey_step_element_option_score_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "SurveyStepElementOptionScoreAudit",
      "through": null
    },
    {
      "className": "SurveyStepElementOption",
      "collectionName": "survey_step_element_options",
      "foreignKey": "survey_step_element_option_id",
      "name": "option",
      "macro": "belongs_to",
      "resource_name": "SurveyStepElementOption",
      "through": null
    },
    {
      "className": "ScoreFactor",
      "collectionName": "score_factors",
      "foreignKey": "score_factor_id",
      "name": "score_factor",
      "macro": "belongs_to",
      "resource_name": "ScoreFactor",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "score",
      "column": {
        "default": null,
        "name": "score",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "score_factor_id",
      "column": {
        "default": null,
        "name": "score_factor_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "survey_step_element_option_id",
      "column": {
        "default": null,
        "name": "survey_step_element_option_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "SurveyStepElementOption",
      "collectionName": "survey_step_element_options",
      "foreignKey": "survey_step_element_option_id",
      "name": "option",
      "macro": "belongs_to",
      "resource_name": "SurveyStepElementOption",
      "through": null
    },
    {
      "className": "ScoreFactor",
      "collectionName": "score_factors",
      "foreignKey": "score_factor_id",
      "name": "score_factor",
      "macro": "belongs_to",
      "resource_name": "ScoreFactor",
      "through": null
    }
  ],
  "paramKey": "survey_step_element_option_score",
  "primaryKey": "id"
}

/** Frontend model for SurveyStepElementOptionScore. */
export default class SurveyStepElementOptionScore extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {number} */
  score() {
    return this.readAttributeUnderscore("score")
  }

  /** @returns {boolean} */
  hasScore() {
    const value = this.score()

    return this._isPresent(value)
  }

  /** @returns {any} */
  scoreFactorId() {
    return this.readAttributeUnderscore("score_factor_id")
  }

  /** @returns {boolean} */
  hasScoreFactorId() {
    const value = this.scoreFactorId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  surveyStepElementOptionId() {
    return this.readAttributeUnderscore("survey_step_element_option_id")
  }

  /** @returns {boolean} */
  hasSurveyStepElementOptionId() {
    const value = this.surveyStepElementOptionId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./survey-step-element-option.js").default | null} */
  option() {
    return this._readBelongsToReflection({modelClass: SurveyStepElementOption, reflectionName: "option"})
  }

  /** @returns {Promise<import("./survey-step-element-option.js").default | null>} */
  loadOption() {
    if (!("surveyStepElementOptionId" in this)) throw new Error("Foreign key method wasn't defined: surveyStepElementOptionId")

    const id = this.surveyStepElementOptionId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "option",
        model: this,
        modelClass: SurveyStepElementOption
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./survey-step-element-option.js").default | null>} */
  ensureOptionLoaded() {
    return this.ensureAssociationLoaded("option")
  }

  /** @returns {import("./score-factor.js").default | null} */
  scoreFactor() {
    return this._readBelongsToReflection({modelClass: ScoreFactor, reflectionName: "score_factor"})
  }

  /** @returns {Promise<import("./score-factor.js").default | null>} */
  loadScoreFactor() {
    if (!("scoreFactorId" in this)) throw new Error("Foreign key method wasn't defined: scoreFactorId")

    const id = this.scoreFactorId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "score_factor",
        model: this,
        modelClass: ScoreFactor
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./score-factor.js").default | null>} */
  ensureScoreFactorLoaded() {
    return this.ensureAssociationLoaded("score_factor")
  }
}
